import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_PHONE_COUNTRY,
  getPhoneCountryOptions,
  normalizePhone,
  phoneCallingCode,
} from '../../../utils/phone.js';
import '../TextField/TextField.less';
import './PhoneField.less';

// Not part of the normalizePhone contract: a form with no extension input
// cannot store one, so an extension typed into the number is refused rather
// than silently dropped.
const EXTENSION_NOT_SUPPORTED = {
  code: 'EXTENSION_NOT_SUPPORTED',
  message: 'Enter the number without an extension.',
};

// What the inputs should show for a stored value: a number that parses is
// shown in its national format under its own country; anything else (a
// legacy value) is shown exactly as stored.
function seed(value, extension, defaultCountry) {
  const stored = typeof value === 'string' ? value : '';
  const result = stored.trim() ? normalizePhone(stored, { defaultCountry }) : null;
  if (result?.ok) {
    return {
      country: result.country || defaultCountry,
      text: result.national,
      ext: extension || result.extension || '',
    };
  }
  return { country: defaultCountry, text: stored, ext: extension || '' };
}

function evaluate({ country, text, ext }, withExtension) {
  const raw = text;
  const extension = withExtension ? (ext.trim() || null) : null;
  if (!raw.trim()) {
    return { e164: '', extension, country, raw, valid: false, empty: true, code: 'EMPTY', message: null };
  }
  const result = normalizePhone(raw, { defaultCountry: country });
  if (!result.ok) {
    return { e164: '', extension, country, raw, valid: false, empty: false, code: result.code, message: result.message };
  }
  if (result.extension && !withExtension) {
    return { e164: '', extension: null, country: result.country || country, raw, valid: false, empty: false, ...EXTENSION_NOT_SUPPORTED };
  }
  return {
    e164: result.e164,
    extension: extension || result.extension || null,
    country: result.country || country,
    raw,
    valid: true,
    empty: false,
    code: null,
    message: null,
    national: result.national,
  };
}

/**
 * PhoneField — the one phone input: country selector (name + calling
 * code), national-number input and, with `withExtension`, a separate
 * extension input. Sibling of TextField and styled by the same tokens.
 *
 * `value` is the stored number (E.164, or a legacy value shown as-is).
 * `onChange` receives `{ e164, extension, country, raw, valid }` plus
 * `empty`, `code` and `message` — `e164` is '' until the number is valid,
 * and `raw` is always exactly what is in the input.
 *
 * The number is checked on blur; a form validates on submit by reading the
 * last `onChange` payload and passing its `message` back as `error`.
 */
function PhoneField({
  label,
  hint,
  error,
  name,
  value,
  extension,
  onChange,
  onBlur,
  defaultCountry = DEFAULT_PHONE_COUNTRY,
  withExtension = false,
  required,
  disabled,
  autoFocus,
  className = '',
}) {
  const uid = useId();
  const inputId = `${uid}-${name || 'phone'}`;
  const hintId = hint ? `${inputId}-hint` : undefined;

  const [state, setState] = useState(() => seed(value, extension, defaultCountry));
  const [blurError, setBlurError] = useState(null);
  // The last value this field reported, so a `value` prop that merely echoes
  // it back is not mistaken for a new stored value.
  const reported = useRef({ e164: value, raw: value });

  useEffect(() => {
    const incoming = typeof value === 'string' ? value : '';
    if (incoming === (reported.current.e164 ?? '') || incoming === (reported.current.raw ?? '')) return;
    reported.current = { e164: incoming, raw: incoming };
    setState(seed(incoming, extension, defaultCountry));
    setBlurError(null);
    // Re-seed only when the stored value itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // ~245 <option>s: built once, so typing in the number does not re-render them.
  const countryOptions = useMemo(() => {
    const { primary, other } = getPhoneCountryOptions();
    const renderOption = (o) => (
      <option key={o.code} value={o.code}>{`${o.name} (+${o.callingCode})`}</option>
    );
    return (
      <>
        {primary.map(renderOption)}
        <optgroup label="All countries">
          {other.map(renderOption)}
        </optgroup>
      </>
    );
  }, []);
  const shownError = error || blurError;
  const errorId = shownError ? `${inputId}-err` : undefined;

  function commit(next, { checkNow = false } = {}) {
    const result = evaluate(next, withExtension);
    // A number entered internationally says which country it belongs to.
    const settled = result.valid && result.country !== next.country
      ? { ...next, country: result.country }
      : next;
    setState(settled);
    setBlurError(checkNow && !result.valid && !result.empty ? result.message : null);
    reported.current = { e164: result.e164, raw: result.raw };
    onChange?.({ ...result, country: settled.country });
    return result;
  }

  function onPaste(e) {
    const pasted = e.clipboardData?.getData('text') || '';
    const result = normalizePhone(pasted, { defaultCountry: state.country });
    if (!result.ok) return; // not a whole number — let it paste as typed text
    if (result.extension && !withExtension) return;
    e.preventDefault();
    // A complete number replaces the field: its own country is selected and
    // only the national part is kept, so the calling code is never doubled.
    commit({
      country: result.country || state.country,
      text: result.national,
      ext: result.extension || state.ext,
    });
  }

  function onNumberBlur(e) {
    const result = evaluate(state, withExtension);
    if (result.valid) {
      // Tidy what was typed: national format, extension in its own input.
      const tidy = { country: result.country, text: result.national, ext: result.extension || '' };
      if (tidy.text !== state.text || tidy.ext !== state.ext || tidy.country !== state.country) {
        commit(tidy);
      }
      setBlurError(null);
    } else {
      // Invalid input is left exactly as typed.
      setBlurError(result.empty ? null : result.message);
    }
    onBlur?.(e);
  }

  const callingCode = phoneCallingCode(state.country);

  return (
    <div className={`gc-field gc-phone-field ${className}`.trim()}>
      {label && (
        <label htmlFor={inputId} className="gc-field-label">
          {label}
          {required && <span className="gc-field-required" aria-hidden="true">*</span>}
        </label>
      )}
      <div className="gc-phone-field-row">
        <div className={`gc-phone-field-country${disabled ? ' is-disabled' : ''}`}>
          <span className="gc-phone-field-country-value" aria-hidden="true">
            {state.country} +{callingCode}
          </span>
          <select
            className="gc-phone-field-country-select"
            aria-label="Country"
            value={state.country}
            onChange={(e) => commit({ ...state, country: e.target.value }, { checkNow: !!blurError })}
            disabled={disabled}
            autoComplete="tel-country-code"
          >
            {countryOptions}
          </select>
        </div>
        <input
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          name={name}
          value={state.text}
          onChange={(e) => commit({ ...state, text: e.target.value })}
          onPaste={onPaste}
          onBlur={onNumberBlur}
          required={required}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-invalid={!!shownError || undefined}
          aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
          className={`gc-field-input gc-phone-field-number${shownError ? ' is-invalid' : ''}`}
        />
        {withExtension && (
          <input
            type="text"
            inputMode="numeric"
            autoComplete="tel-extension"
            name={name ? `${name}Extension` : undefined}
            aria-label="Extension"
            placeholder="Ext."
            value={state.ext}
            onChange={(e) => commit({ ...state, ext: e.target.value.replace(/\D/g, '') })}
            disabled={disabled}
            className="gc-field-input gc-phone-field-extension"
          />
        )}
      </div>
      {hint && !shownError && (
        <p id={hintId} className="gc-field-hint">{hint}</p>
      )}
      {shownError && (
        <p id={errorId} className="gc-field-error" role="alert">{shownError}</p>
      )}
    </div>
  );
}

export default PhoneField;
